"use client";

/**
 * Redirects logged-in business users to the dashboard when they land on the
 * marketing homepage via a direct URL (empty/external referrer).
 *
 * Public content under the marketing layout (/help, pricing, faq, etc.) is
 * allowlisted so owners — including mid-onboarding — can read docs without
 * bouncing to /app (and then onboarding). Help links from onboarding open with
 * rel="noreferrer", which clears document.referrer and previously triggered this.
 */
import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { getToken } from "@/lib/auth";
import { getCurrentUser } from "@/lib/api";

/** Marketing paths that must stay reachable for logged-in / incomplete owners. */
const PUBLIC_CONTENT_PREFIXES = [
  "/help",
  "/pricing",
  "/faq",
  "/features",
  "/contact",
  "/privacy",
  "/cookie-policy",
  "/cookie-preferences",
  "/about",
  "/switch-from-booksy-or-fresha",
] as const;

export function isPublicContentPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return PUBLIC_CONTENT_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function MarketingRedirect() {
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    async function checkAndRedirect() {
      try {
        if (isPublicContentPath(pathname)) return;

        const token = await getToken();
        if (!token) return;

        const user = await getCurrentUser();
        if (!user || !user.businessId) return;

        // Incomplete owners must not be sent to /app/dashboard — AppLayoutWithGuard
        // would immediately bounce them to /app/onboarding.
        if (!(user.business?.onboardingComplete ?? false)) return;

        const referrer = document.referrer;
        const currentOrigin = window.location.origin;

        // Direct / external entry on non-allowlisted marketing (e.g. /) → dashboard
        if (!referrer || !referrer.startsWith(currentOrigin)) {
          router.replace("/app/dashboard");
        }
      } catch {
        // Ignore errors (e.g. API unavailable)
      }
    }

    checkAndRedirect();
  }, [router, pathname]);

  return null;
}
